import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../../services/apiService';
import Dialogo from '../../components/Dialogo';
import '../MetasConfig.css';
import './Escalas.css';
import {
    SCOPES, SCOPE_LABEL, addDias, agruparHerancia, atribuicaoParaForm, atribuicaoParaPayload, atribuicaoVazia, brDate,
    campoTravado, cargaDeHistorico, diasEntre, exigirForma, hojeISO, mensagemErro, textoHerancia, validarAtribuicao,
    resumoHorario,
} from './escalasUtil';

/** Dia do ciclo rotativo, como a API calcula: ((dia - âncora) + deslocamento) mod tamanho. */
function previaRotativa(padrao, ancora, deslocamento, n = 14) {
    const out = [];
    for (let i = 0; i < n; i += 1) {
        const dia = addDias(ancora, i);
        const idx = (((diasEntre(ancora, dia) + Number(deslocamento || 0)) % padrao.cycle_length) + padrao.cycle_length) % padrao.cycle_length;
        const d = padrao.days.find((x) => x.day_index === idx);
        out.push({ dia, trabalha: !!(d && d.is_working), texto: d && d.is_working ? resumoHorario(d.start_time, d.end_time) : 'Folga' });
    }
    return out;
}

/**
 * Aba Atribuições: qual padrão vale para a empresa, uma unidade, uma loja ou um vendedor, e a partir
 * de quando. Cascata: vendedor > loja > unidade > empresa; no mesmo escopo vale a vigência mais
 * recente. Trocar de escala é encerrar a atribuição atual (fim da vigência) e começar outra.
 */
function AtribuicoesTab({ subdomain, lookups, notify }) {
    const { stores, salesmen, units, nomeLoja, nomeUnidade } = lookups;
    const [lista, setLista] = useState([]);
    const [padroes, setPadroes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [erroApi, setErroApi] = useState('');
    const [paraExcluir, setParaExcluir] = useState(null);
    const [paraEncerrar, setParaEncerrar] = useState(null);   // atribuição que está sendo encerrada
    const [fimEncerrar, setFimEncerrar] = useState('');
    const [encerrando, setEncerrando] = useState(false);
    const [primeiroAberto, setPrimeiroAberto] = useState(null);   // first_open_day da API: dia anterior a ele é fechado
    const savingRef = useRef(false);

    const [lojaCascata, setLojaCascata] = useState('');
    const [heranca, setHeranca] = useState(null);
    const [carregandoHeranca, setCarregandoHeranca] = useState(false);

    const carregar = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            const [a, p] = await Promise.all([
                apiService.scheduleListAssignments({ subdomain }),
                apiService.scheduleListPatterns({ subdomain }),
            ]);
            setLista(exigirForma(a, 'assignments').assignments);
            setPrimeiroAberto(a.first_open_day || null);
            setPadroes(exigirForma(p, 'patterns').patterns);
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao carregar as atribuições'), 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, notify]);

    useEffect(() => { setForm(null); setLista([]); setHeranca(null); setLojaCascata(''); carregar(); }, [carregar]);

    const padraoSel = useMemo(() => (form ? padroes.find((p) => String(p.id) === String(form.pattern_id)) : null), [form, padroes]);
    // Atribuição com dia fechado: o fim só pode ser ontem ou depois (o passado não muda).
    const menorFim = primeiroAberto ? addDias(primeiroAberto, -1) : null;
    const erroFim = form && form.id && form.has_closed_days && form.valid_to && menorFim && form.valid_to < menorFim
        ? `O fim não pode cortar dias fechados: use ${brDate(menorFim)} ou depois`
        : null;
    const erro = form ? (validarAtribuicao(form, padraoSel, primeiroAberto) || erroFim) : null;
    const retro = cargaDeHistorico(form, primeiroAberto);
    const travado = (campo) => campoTravado(form, campo);
    const setCampo = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const abrirNovo = () => { setErroApi(''); setForm(atribuicaoVazia()); };
    const abrirEdicao = (a) => {
        setErroApi('');
        setForm(atribuicaoParaForm(a));
    };
    const fechar = () => { setForm(null); setErroApi(''); };

    const salvar = async () => {
        if (erro) { notify(erro, 'error'); return; }
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setErroApi('');
        try {
            const r = await apiService.scheduleSaveAssignment(atribuicaoParaPayload(form, subdomain, padraoSel, primeiroAberto));
            if (!r || r.success !== true) throw new Error('resposta fora de forma');
            notify(form.id ? 'Atribuição atualizada' : 'Atribuição criada', 'success');
            fechar();
            carregar();
        } catch (e) {
            // 409 (sobreposição, mesma data de início, conflito de unidades) vem em português e
            // explica a saída; fica dentro do formulário, onde a pessoa ainda pode corrigir.
            setErroApi(mensagemErro(e, 'Erro ao salvar a atribuição'));
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const excluir = async () => {
        const a = paraExcluir;
        try {
            await apiService.scheduleDeleteAssignment({ subdomain, id: a.id });
            notify('Atribuição removida', 'success');
            setParaExcluir(null);
            carregar();
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao remover a atribuição'), 'error');
            setParaExcluir(null);
        }
    };

    // Encerrar = mudar só o fim da vigência. É o que se faz com atribuição que já tem dia fechado.
    const podeEncerrar = (a) => !Array.isArray(a.editable_fields) || a.editable_fields.includes('valid_to');
    const abrirEncerrar = (a) => {
        setFimEncerrar(menorFim && (!a.valid_to || a.valid_to > menorFim) ? menorFim : (a.valid_to || hojeISO()));
        setParaEncerrar(a);
    };
    const erroEncerrar = paraEncerrar
        ? (!fimEncerrar ? 'Informe o último dia em que a atribuição vale'
            : fimEncerrar < String(paraEncerrar.valid_from).split('T')[0] ? 'O fim não pode ser anterior ao início'
                : (menorFim && fimEncerrar < menorFim ? `Dias anteriores a ${brDate(primeiroAberto)} já estão fechados: use ${brDate(menorFim)} ou depois` : null))
        : null;
    const encerrar = async () => {
        const a = paraEncerrar;
        if (!a || erroEncerrar || encerrando) return;
        setEncerrando(true);
        try {
            const pad = padroes.find((p) => String(p.id) === String(a.pattern_id)) || null;
            const r = await apiService.scheduleSaveAssignment(
                atribuicaoParaPayload({ ...atribuicaoParaForm(a), valid_to: fimEncerrar }, subdomain, pad, primeiroAberto),
            );
            if (!r || r.success !== true) throw new Error('resposta fora de forma');
            notify(`Atribuição encerrada em ${brDate(fimEncerrar)}`, 'success');
            setParaEncerrar(null);
            carregar();
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao encerrar a atribuição'), 'error');
        } finally {
            setEncerrando(false);
        }
    };

    const alvoTexto = (a) => {
        if (a.scope === 'company') return 'Empresa toda';
        if (a.scope === 'unit') return nomeUnidade(a.unit_id);
        if (a.scope === 'store') return a.store_name || nomeLoja(a.store_id);
        return a.salesman_name || `Vendedor ${a.salesman_id}`;
    };

    const carregarHeranca = async (storeId) => {
        setLojaCascata(storeId);
        setHeranca(null);
        if (!storeId) return;
        setCarregandoHeranca(true);
        try {
            const hoje = hojeISO();
            const r = await apiService.scheduleResolve({ subdomain, store_id: Number(storeId), date_from: hoje, date_to: hoje });
            if (!r || r.success !== true || !Array.isArray(r.salesmen)) throw new Error('resposta fora de forma');
            setHeranca(r.salesmen);
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao consultar a cascata da loja'), 'error');
        } finally {
            setCarregandoHeranca(false);
        }
    };

    const unidadesDaLoja = useMemo(
        () => units.filter((u) => (u.store_ids || []).map(Number).includes(Number(lojaCascata))),
        [units, lojaCascata],
    );
    const unidadesComEscala = useMemo(() => {
        const hoje = hojeISO();
        return unidadesDaLoja.filter((u) => lista.some((a) => a.scope === 'unit' && a.active
            && String(a.unit_id) === String(u.id) && a.valid_from <= hoje && (!a.valid_to || a.valid_to >= hoje)));
    }, [unidadesDaLoja, lista]);
    const grupos = heranca ? agruparHerancia(heranca, lista) : [];

    const padroesParaEscolha = padroes.filter((p) => p.active || String(p.id) === String(form?.pattern_id));
    const previa = form && padraoSel && padraoSel.cycle_type === 'rotating' && form.anchor_date
        ? previaRotativa(padraoSel, form.anchor_date, form.cycle_offset) : null;

    return (
        <div className="mc-page">
            <p className="mc-nota">
                A escala de um dia vem da atribuição <strong>mais específica</strong>: vendedor, depois loja,
                depois unidade, depois empresa. No mesmo escopo vale a de início mais recente. Para trocar de
                escala, encerre a atual (fim da vigência) e crie outra a partir da nova data: o passado não muda. Atribuição
                que já tem dias fechados só aceita mudar o rótulo e o fim; não se apaga, se encerra.
            </p>

            <div className="mc-filters">
                <div className="mc-field mc-filters-actions esc-acoes">
                    <button className="mc-btn mc-btn-primary" onClick={abrirNovo} disabled={!subdomain || padroes.length === 0}>
                        ➕ Nova atribuição
                    </button>
                </div>
            </div>
            {subdomain && !loading && padroes.length === 0 && (
                <p className="mc-nota">Cadastre um padrão na aba Padrões antes de atribuir.</p>
            )}

            {form && (
                <div className="mc-form-panel">
                    <h2>{form.id ? 'Editar atribuição' : 'Nova atribuição'}</h2>
                    {form.id && form.has_closed_days && (
                        <p className="esc-aviso" role="note">
                            Esta atribuição já tem dias fechados{primeiroAberto ? ` (antes de ${brDate(primeiroAberto)})` : ''}, e o
                            passado não muda. Só o rótulo e o fim da vigência podem ser alterados. Para trocar de padrão, encerre esta
                            e crie outra{primeiroAberto ? ` a partir de ${brDate(primeiroAberto)}` : ''}.
                        </p>
                    )}
                    {retro && (
                        <p className="esc-aviso" role="note">
                            O início é anterior a {brDate(primeiroAberto)}: isto é uma <strong>carga de histórico</strong>. Só vale se
                            o alvo nunca teve outra atribuição, exige motivo e só quem está autorizado a corrigir o passado pode
                            fazê-la. Os dias passados ficam congelados na hora. Se já houve atribuição, use exceções.
                        </p>
                    )}
                    <div className="mc-form-grid">
                        <div className="mc-field">
                            <label htmlFor="esc-at-escopo">Vale para *</label>
                            <select id="esc-at-escopo" value={form.scope} disabled={travado('scope')}
                                onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value, unit_id: '', store_id: '', salesman_id: '' }))}>
                                {SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </div>
                        {form.scope === 'unit' && (
                            <div className="mc-field">
                                <label htmlFor="esc-at-unit">Unidade *</label>
                                <select id="esc-at-unit" value={form.unit_id} disabled={travado('scope')} onChange={(e) => setCampo('unit_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {units.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                                </select>
                            </div>
                        )}
                        {form.scope === 'store' && (
                            <div className="mc-field">
                                <label htmlFor="esc-at-loja">Loja *</label>
                                <select id="esc-at-loja" value={form.store_id} disabled={travado('scope')} onChange={(e) => setCampo('store_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                                </select>
                            </div>
                        )}
                        {form.scope === 'salesman' && (
                            <div className="mc-field">
                                <label htmlFor="esc-at-vend">Vendedor *</label>
                                <select id="esc-at-vend" value={form.salesman_id} disabled={travado('scope')} onChange={(e) => setCampo('salesman_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {salesmen.map((s) => (
                                        <option key={s.salesman_id} value={s.salesman_id}>
                                            {s.salesman_name} — {s.store_name}{s.is_active ? '' : ' (inativo)'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="mc-field">
                            <label htmlFor="esc-at-padrao">Padrão *</label>
                            <select id="esc-at-padrao" value={form.pattern_id} disabled={travado('pattern_id')} onChange={(e) => setCampo('pattern_id', e.target.value)}>
                                <option value="">— Selecione —</option>
                                {padroesParaEscolha.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}{p.active ? '' : ' (inativo)'}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mc-field">
                            <label htmlFor="esc-at-de">Vale a partir de *</label>
                            <input id="esc-at-de" type="date" value={form.valid_from} disabled={travado('valid_from')} onChange={(e) => setCampo('valid_from', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label htmlFor="esc-at-ate">Até (vazio = sem fim)</label>
                            <input id="esc-at-ate" type="date" value={form.valid_to} disabled={travado('valid_to')}
                                min={form.id && form.has_closed_days && menorFim ? menorFim : undefined} onChange={(e) => setCampo('valid_to', e.target.value)} />
                        </div>
                        {padraoSel && padraoSel.cycle_type === 'rotating' && (
                            <>
                                <div className="mc-field">
                                    <label htmlFor="esc-at-ancora">Data âncora * (ciclo no dia 1)</label>
                                    <input id="esc-at-ancora" type="date" value={form.anchor_date} disabled={travado('anchor_date')} onChange={(e) => setCampo('anchor_date', e.target.value)} />
                                    <small className="esc-dica">Uma data em que a pessoa começa o ciclo. Dali em diante o ciclo se repete.</small>
                                </div>
                                <div className="mc-field">
                                    <label htmlFor="esc-at-off">Deslocamento do ciclo (0 a {padraoSel.cycle_length - 1})</label>
                                    <input id="esc-at-off" type="number" min="0" max={padraoSel.cycle_length - 1} value={form.cycle_offset}
                                        disabled={travado('cycle_offset')} onChange={(e) => setCampo('cycle_offset', e.target.value)} />
                                    <small className="esc-dica">Para deslocar a folga de quem divide o mesmo padrão e a mesma âncora.</small>
                                </div>
                            </>
                        )}
                        <div className="mc-field">
                            <label htmlFor="esc-at-rotulo">Rótulo (opcional)</label>
                            <input id="esc-at-rotulo" type="text" value={form.label} onChange={(e) => setCampo('label', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label><input type="checkbox" checked={form.active} disabled={travado('active')} onChange={(e) => setCampo('active', e.target.checked)} /> Ativa</label>
                        </div>
                        {retro && (
                            <div className="mc-field mc-field-wide">
                                <label htmlFor="esc-at-motivo">Motivo da carga de histórico *</label>
                                <input id="esc-at-motivo" type="text" maxLength={240} value={form.reason}
                                    onChange={(e) => setCampo('reason', e.target.value)} />
                            </div>
                        )}
                    </div>

                    {previa && (
                        <>
                            <h3>Próximos 14 dias a partir da âncora</h3>
                            <div className="esc-previa">
                                {previa.map((d) => (
                                    <div key={d.dia} className={`esc-previa-dia ${d.trabalha ? 'esc-dia-work' : 'esc-dia-off'}`}>
                                        <strong>{brDate(d.dia).slice(0, 5)}</strong>
                                        <span>{d.texto}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {erro && <p className="mc-nota">{erro}</p>}
                    {erroApi && <p className="esc-erro" role="alert">{erroApi}</p>}
                    <div className="mc-form-actions">
                        <button className="mc-btn" onClick={fechar} disabled={saving}>Cancelar</button>
                        <button className="mc-btn mc-btn-primary" onClick={salvar} disabled={saving || !!erro}>
                            {saving ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                </div>
            )}

            <div className="mc-list">
                <h2>Atribuições {loading ? '(carregando…)' : `(${lista.length})`}</h2>
                {!subdomain && <p className="mc-empty">Selecione uma empresa.</p>}
                {subdomain && !loading && lista.length === 0 && <p className="mc-empty">Nenhuma atribuição cadastrada.</p>}
                {lista.length > 0 && (
                    <div className="esc-tabela-rolagem">
                        <table className="mc-table">
                            <thead>
                                <tr><th>Vale para</th><th>Alvo</th><th>Padrão</th><th>Início</th><th>Fim</th><th>Âncora</th><th>Desl.</th><th>Ativa</th><th></th></tr>
                            </thead>
                            <tbody>
                                {lista.map((a) => (
                                    <tr key={a.id}>
                                        <td>{SCOPE_LABEL[a.scope] || a.scope}</td>
                                        <td>{alvoTexto(a)}{a.label ? <small> — {a.label}</small> : null}</td>
                                        <td>{a.pattern_name}</td>
                                        <td>
                                            {brDate(a.valid_from)}
                                            {a.has_closed_days && (
                                                <span className="esc-selo-fechado" title="Já tem dias fechados: o passado não muda; só rótulo e fim da vigência">dias fechados</span>
                                            )}
                                        </td>
                                        <td>{a.valid_to ? brDate(a.valid_to) : 'sem fim'}</td>
                                        <td>{a.anchor_date ? brDate(a.anchor_date) : '—'}</td>
                                        <td>{a.anchor_date ? a.cycle_offset : '—'}</td>
                                        <td>{a.active ? 'Sim' : 'Não'}</td>
                                        <td className="mc-actions">
                                            <button className="mc-btn mc-btn-sm" onClick={() => abrirEdicao(a)}>✏️ Editar</button>
                                            {a.has_closed_days && podeEncerrar(a) && (
                                                <button className="mc-btn mc-btn-sm" onClick={() => abrirEncerrar(a)}>⏹️ Encerrar</button>
                                            )}
                                            <button className="mc-btn mc-btn-sm mc-btn-danger" onClick={() => setParaExcluir(a)}
                                                disabled={!!a.has_closed_days}
                                                title={a.has_closed_days ? 'Tem dias fechados: encerre em vez de apagar' : undefined}>
                                                🗑️ Remover
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="mc-form-panel">
                <h2>Quem herda o quê, hoje</h2>
                <p className="esc-dica">Escolha uma loja para ver de qual atribuição cada vendedor recebe a escala de hoje.</p>
                <div className="mc-field esc-campo-medio">
                    <label htmlFor="esc-heranca-loja">Loja</label>
                    <select id="esc-heranca-loja" value={lojaCascata} onChange={(e) => carregarHeranca(e.target.value)}>
                        <option value="">— Selecione —</option>
                        {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                </div>
                {carregandoHeranca && <p className="esc-dica">Consultando…</p>}
                {lojaCascata && unidadesDaLoja.length > 0 && (
                    <p className="esc-dica">Unidades desta loja: {unidadesDaLoja.map((u) => u.label).join(', ')}.</p>
                )}
                {unidadesComEscala.length > 1 && (
                    <p className="esc-aviso">
                        Atenção: esta loja está em {unidadesComEscala.length} unidades que têm atribuição vigente
                        ({unidadesComEscala.map((u) => u.label).join(', ')}). Vale a de menor id; o ideal é deixar só uma.
                    </p>
                )}
                {heranca && grupos.length === 0 && <p className="mc-empty">Nenhum vendedor ativo nesta loja.</p>}
                {grupos.length > 0 && (
                    <ul className="esc-lista-heranca">
                        {grupos.map((g) => (
                            <li key={String(g.assignment_id)}>
                                {textoHerancia(g, g.unit_id != null ? nomeUnidade(g.unit_id) : null)}
                                <small>{g.nomes.slice(0, 8).join(', ')}{g.nomes.length > 8 ? `… (+${g.nomes.length - 8})` : ''}</small>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {paraEncerrar && (
                <Dialogo titulo="Encerrar atribuição" rotuloConfirmar="Encerrar" rotuloOcupado="Encerrando…"
                    ocupado={encerrando} confirmarHabilitado={!erroEncerrar}
                    onCancelar={() => setParaEncerrar(null)} onConfirmar={encerrar}>
                    <p>
                        Encerrar a atribuição de <strong>{paraEncerrar.pattern_name}</strong> para <strong>{alvoTexto(paraEncerrar)}</strong>.
                        Os dias já fechados continuam como estavam; depois do último dia ela deixa de valer.
                    </p>
                    <label htmlFor="esc-at-fim">Último dia em que vale</label>
                    <input id="esc-at-fim" type="date" value={fimEncerrar} min={menorFim || undefined}
                        onChange={(e) => setFimEncerrar(e.target.value)} />
                    <p className="dlg-efeito">
                        {erroEncerrar || (fimEncerrar ? `A atribuição vale até ${brDate(fimEncerrar)}.` : '')}
                    </p>
                    <p className="dlg-nota">Para continuar com outra escala, crie uma nova atribuição a partir do dia seguinte.</p>
                </Dialogo>
            )}

            {paraExcluir && (
                <Dialogo titulo="Remover atribuição" perigo rotuloConfirmar="Remover"
                    onCancelar={() => setParaExcluir(null)} onConfirmar={excluir}>
                    <p>
                        Remover a atribuição de <strong>{paraExcluir.pattern_name}</strong> para <strong>{alvoTexto(paraExcluir)}</strong>?
                        Os dias voltam a vir da atribuição de escopo acima, ou ficam sem escala. Para só encerrar daqui para frente,
                        edite e preencha “Até”.
                    </p>
                </Dialogo>
            )}
        </div>
    );
}

export default AtribuicoesTab;
